import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { protectedRoutes } from 'src/config/routes';
import { toast } from 'src/hooks/use-toast';
import { authApi, logout, refreshToken } from 'src/lib/api/auth/auth.api';
import { authEventManager } from 'src/lib/api/authEventManager';
import { api } from 'src/lib/api/axios.interceptor';
import { API_ENDPOINTS } from 'src/lib/api/constants';
import { useToast } from 'src/lib/toast-context';
import { getDefaultRouteForRole } from 'src/lib/utils';
import { AuthState, LoginCredentials, User } from 'src/types/auth.types'; // Assuming User is exported
import { RouteConfig } from 'src/types/route-config.types';
import { API_RESP_MESSAGE } from './messages';

// --- NEW: Helper to check for mock mode ---
// For Create React App: process.env.REACT_APP_MOCK_AUTH
// For Vite: import.meta.env.VITE_MOCK_AUTH
const IS_MOCK_MODE = process.env.REACT_APP_MOCK_AUTH === 'true';

// --- NEW: Mock user and token data ---
const mockUser: User = {
    _id: 'mock-user-123',
    usercode: "1234",
    name: 'Mock User',
    email: 'mock.user@example.com',
    role: 'admin',
    discom: "DISCOM",
    division: "executive"
    // Add any other user properties you need
};

const mockTokens = {
    accessToken: 'mock-access-token-string',
    refreshToken: 'mock-refresh-token-string',
};

// --- NEW: Mock functions to replace real ones in mock mode ---
const mockLogin = async (credentials: LoginCredentials): Promise<void> => {
    console.log('Mock login called with:', credentials);
    // In mock mode, login does nothing as the user is already "logged in"
    return Promise.resolve();
};

const mockLogout = async (): Promise<void> => {
    console.log('Mock logout called.');
    // In mock mode, a page refresh is the easiest way to "reset"
    // Or just do nothing if you want the session to persist
    window.location.reload();
    return Promise.resolve();
};

const mockRefreshUser = async (): Promise<void> => {
    console.log('Mock refreshUser called.');
    return Promise.resolve();
};


// --- The context and provider ---
interface AuthContextType extends AuthState {
    login: (credentials: LoginCredentials) => Promise<void>;
    logout: () => Promise<void>;
    refreshUser: () => Promise<void>;
    setUnAuthorized: (status?: boolean) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
    children
}) => {
    const [state, setState] = useState<AuthState>({
        user: null,
        isAuthenticated: false,
        isLoading: true, // Start with loading true
        error: null,
        isLoginIng: false,
        unAuthorized: false,
        isRefreshing: false
    });

    const { showToast } = useToast()

    const navigate = useNavigate();

    const { pathname, search } = useLocation()

    // Initialize auth state
    useEffect(() => {
        const initAuth = async () => {
            try {
                const _refreshToken = localStorage.getItem("refreshToken")
                // console.log("Auth Init :", { currentPath: pathname, _refreshToken });
                if (_refreshToken) {
                    const resp: any = _refreshToken && await api.post(API_ENDPOINTS.AUTH.REFRESH, { refreshToken: _refreshToken });
                    // console.log({ resp });
                    const { user, tokens }: any = resp.data?.data
                    localStorage.setItem('accessToken', tokens?.access?.token);
                    localStorage.setItem('refreshToken', tokens?.refresh?.token);
                    // console.log({ user, tokens });

                    setState({ user, isAuthenticated: true, isLoading: false, isLoginIng: false, error: null, });
                    if (pathname === "/auth/login") {
                        navigate("/")
                    }
                    else navigate(pathname + search || "/")
                }
                else {
                    const PRIVATE_ROUTES = protectedRoutes.reduce((acc: any, route: RouteConfig) => {
                        if (route.children && route.children?.length > 0) {
                            const allRoutes = route.children?.map((subRoute: RouteConfig) => route.path + subRoute.path)
                            acc.push(...allRoutes)
                        }
                        else acc.push(route.path)
                        return acc
                    }, [])

                    // console.log({ PRIVATE_ROUTES, pathname });
                    if (pathname == "/" || PRIVATE_ROUTES.includes(pathname)) {
                        navigate("/auth/login")
                    }
                    else navigate(pathname + search)
                    setState({
                        ...state,
                        isLoading: false
                    })
                }
            } catch (error: any) {
                setState({
                    ...state,
                    isLoading: false,
                    isLoginIng: false,
                    error: "Session Expired"
                })
                logout()
                showToast(error.response?.data?.message || "A server error occurred", {
                    variant: "error"
                })
            }
        };
        initAuth();
    }, []);

    useEffect(() => {
        authEventManager.on("auth-refresh", refreshUser)
        return () => {
            authEventManager.off('auth-refresh', refreshUser);
        }
    }, [])


    // --- REAL API Functions (unchanged logic) ---
    const handleLogin = useCallback(async (credentials: LoginCredentials) => {
        setState(prev => ({ ...prev, isLoading: false, isLoginIng: true, error: null }));
        try {
            const resp = await authApi.login(credentials);
            console.log({resp});
            
            const { user, tokens } = resp.data
            localStorage.setItem('accessToken', tokens?.access?.token);
            localStorage.setItem('refreshToken', tokens?.refresh?.token);
            setState({ user, isAuthenticated: true, isLoading: false, isLoginIng: false, error: null });
            let route = getDefaultRouteForRole(user.role, protectedRoutes)
            navigate(route)

        } catch (error: any) {
            // console.log({ response: error });

            setState({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                isLoginIng: false,
                error: error?.message || 'Login failed',
            });

            showToast(error?.code && API_RESP_MESSAGE(error?.code) || "A server error occurred", {
                variant: "error"
            })
        }
    }, [navigate]);

    const handleLogout = useCallback(async () => {
        try {
            await logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            setState({ user: null, isAuthenticated: false, isLoading: false, isLoginIng: false, error: null });
            navigate('/auth/login');
        }
    }, [navigate]);

    const refreshUser = useCallback(async () => {
        const _refreshToken = localStorage.getItem("refreshToken")
        if (!state.isAuthenticated) return;
        try {
            if (_refreshToken) {
                const resp: any = _refreshToken && await api.post(API_ENDPOINTS.AUTH.REFRESH, { refreshToken: _refreshToken });
                console.log({ resp });
                const { user, tokens }: any = resp.data?.data
                localStorage.setItem('accessToken', tokens?.access?.token);
                localStorage.setItem('refreshToken', tokens?.refresh?.token);
                // console.log({ user, tokens });
                authEventManager.emit('refresh-success');
                setState({ user, isAuthenticated: true, isLoading: false, isLoginIng: false, error: null });
                if (pathname === "/auth/login") {
                    navigate("/")
                }
                else navigate(pathname + search || "/")
            }
            else {
                handleLogout()
            }

        } catch (error) {
            console.error('Failed to refresh user:', error);
        }
    }, [state.isAuthenticated]);

    const setUnAuthorized = (status?: boolean) => {
        setState(prev => ({
            ...prev,
            unAuthorized: status || false
        }));
    }

    // --- MODIFIED: Conditionally provide mock or real functions ---
    const value: AuthContextType = {
        ...state,
        login: IS_MOCK_MODE ? mockLogin : handleLogin,
        logout: IS_MOCK_MODE ? mockLogout : handleLogout,
        refreshUser: IS_MOCK_MODE ? mockRefreshUser : refreshUser,
        setUnAuthorized: setUnAuthorized
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};